import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useEmailApiClient } from '@/api/clients/useEmailApiClient.util.ts';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { EMAILS_REDUCER_EVENT, useEmailsContext } from '@/views/emails/context/EmailsContext.ts';
import {
  removeThreadFromList,
  scheduleThreadListRefresh,
} from '@/views/emails/util/threadListCache.util.ts';
import { useCurrentThreadsQueryKey } from '@/views/emails/util/useCurrentThreadsQueryKey.util.ts';
import { ThreadsInfiniteData } from '@/views/emails/util/useReadEmails.util.tsx';

type Req = {
  emailThreadId: string;
  sourceUserEmailFolderId: string;
  targetUserEmailFolderId: string;
};

type Response = void;

type Return = {
  isMoving: boolean;
  moveEmailsToFolder: (req: Req) => Promise<Response>;
};

export const useMoveEmailsToFolder = (): Return => {
  const { emailUpdateApi } = useEmailApiClient();
  const queryClient = useQueryClient();
  const { emailsState, updateEmailState } = useEmailsContext();
  const currentThreadsQueryKey = useCurrentThreadsQueryKey();
  const { userId } = useUserContext();

  const { isPending, mutateAsync } = useMutation<Response, AxiosError, Req>({
    mutationFn: async req => {
      await emailUpdateApi.moveEmailsToFolder(
        userId,
        req.emailThreadId,
        req.targetUserEmailFolderId,
        req.sourceUserEmailFolderId,
      );
    },
    mutationKey: [QueryKeyEnum.EMAILS.MOVE],
    onMutate: async req => {
      if (req.sourceUserEmailFolderId === req.targetUserEmailFolderId) {
        return;
      }
      await queryClient.cancelQueries({ queryKey: currentThreadsQueryKey });
      queryClient.setQueryData<ThreadsInfiniteData>(
        currentThreadsQueryKey,
        removeThreadFromList(req.emailThreadId),
      );
    },
    onSettled: () => {
      scheduleThreadListRefresh(queryClient);
    },
    onSuccess: (_, req) => {
      if (req.emailThreadId === emailsState.selectedThread) {
        updateEmailState({
          payload: null,
          type: EMAILS_REDUCER_EVENT.SELECT_THREAD,
        });
      }
    },
  });

  return {
    isMoving: isPending,
    moveEmailsToFolder: mutateAsync,
  };
};
